import React from 'react';
import { HStack, VStack, Text, Select, SimpleGrid, Button } from '@chakra-ui/react';
import { SchemaPanel, ColorInput, CustomRenderer } from '../SchemaPanel';
import {
  HORA_CLOCK_DESIGN_SCHEMA, SectorColorMode, PlanetColorMap,
} from '../../../config/horaClockConfig';

const PLANET_KEYS: (keyof PlanetColorMap)[] = [
  'Surya', 'Chandra', 'Mangala', 'Budha', 'Guru', 'Shukra', 'Shani',
];
const PLANET_SHORT: Record<string, string> = {
  Surya: 'Sy', Chandra: 'Ch', Mangala: 'Ma', Budha: 'Bu',
  Guru: 'Gu', Shukra: 'Sk', Shani: 'Sa',
};

// ── Custom renderer for sector color mode (conditional UI) ──

const SectorColorModeControl: CustomRenderer = (values, onChange) => {
  const mode = values.colorMode as SectorColorMode;

  return (
    <>
      <HStack justify="space-between">
        <Text fontSize="xs">Mode</Text>
        <Select size="xs" width="120px" variant="filled"
          value={mode} onChange={e => onChange({ colorMode: e.target.value })}
        >
          <option value="uniform">Uniform</option>
          <option value="gradient">Gradient</option>
          <option value="cycle">Cycle</option>
          <option value="per-planet">Per Planet</option>
        </Select>
      </HStack>

      {mode === 'uniform' && (
        <HStack justify="space-between">
          <Text fontSize="xs">Color</Text>
          <ColorInput value={values.uniformColor} onChange={c => onChange({ uniformColor: c })} />
        </HStack>
      )}

      {mode === 'gradient' && (
        <SimpleGrid columns={6} spacing={1}>
          {(values.gradientColors || []).map((c: string, i: number) => (
            <ColorInput key={i} value={c}
              onChange={nc => {
                const cols = [...values.gradientColors];
                cols[i] = nc;
                onChange({ gradientColors: cols });
              }}
              label={`${i + 1}`}
            />
          ))}
        </SimpleGrid>
      )}

      {mode === 'cycle' && (
        <VStack spacing={1} align="stretch">
          <HStack wrap="wrap" spacing={1}>
            {(values.cycleColors || []).map((c: string, i: number) => (
              <ColorInput key={i} value={c}
                onChange={nc => {
                  const cols = [...values.cycleColors];
                  cols[i] = nc;
                  onChange({ cycleColors: cols });
                }}
              />
            ))}
          </HStack>
          <HStack spacing={1}>
            <Button size="xs" variant="ghost"
              onClick={() => {
                if (values.cycleColors.length < 6)
                  onChange({ cycleColors: [...values.cycleColors, '#E2E8F0'] });
              }}
              isDisabled={values.cycleColors.length >= 6}
            >+</Button>
            <Button size="xs" variant="ghost"
              onClick={() => {
                if (values.cycleColors.length > 2)
                  onChange({ cycleColors: values.cycleColors.slice(0, -1) });
              }}
              isDisabled={values.cycleColors.length <= 2}
            >-</Button>
          </HStack>
        </VStack>
      )}

      {mode === 'per-planet' && (
        <SimpleGrid columns={2} spacing={1}>
          {PLANET_KEYS.map(planet => (
            <HStack key={planet} spacing={1}>
              <Text fontSize="xs" minW="22px" color="gray.600">{PLANET_SHORT[planet]}</Text>
              <ColorInput
                value={(values.planetColors || {})[planet] || '#ccc'}
                onChange={c => onChange({
                  planetColors: { ...values.planetColors, [planet]: c },
                })}
              />
            </HStack>
          ))}
        </SimpleGrid>
      )}
    </>
  );
};

const CUSTOM_RENDERERS = {
  sectorColorMode: SectorColorModeControl,
};

export function HoraClockDesignPanel({ chartId }: { chartId: string }) {
  return (
    <SchemaPanel
      schema={HORA_CLOCK_DESIGN_SCHEMA}
      chartId={chartId}
      customRenderers={CUSTOM_RENDERERS}
      tooltip="Hora Clock Design"
    />
  );
}
